import * as React from 'react';
import PartWrapper from '/react4xp/mixins/ui/part-wrapper';
import { Category, NoContentArticle, Section, Tag } from '/react4xp/parts/knowledge-base/knowledge-base.type';
import { getArticlesApi, getCategoriesApi, getTagsApi } from '/react4xp/parts/knowledge-base/actions';
import './knowledge-base.scss';
import { Col, Divider, Empty, Row, Skeleton, Space } from 'antd';
import { useKbEntity } from '/react4xp/parts/knowledge-base/hooks/use-kb-entities';
import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSearch } from '/react4xp/parts/knowledge-base/hooks/use-search';
import TagView from '/react4xp/parts/knowledge-base/components/tag-view';
import SearchBar from '/react4xp/parts/knowledge-base/components/search-bar';
import SectionSelector from '/react4xp/parts/knowledge-base/components/section-selector';
import CategoryList from '/react4xp/parts/knowledge-base/components/category-list';
import ArticleView from '/react4xp/parts/knowledge-base/components/article-view';
import CategoryView from '/react4xp/parts/knowledge-base/components/category-view';
import SearchResultsView from '/react4xp/parts/knowledge-base/components/search-results-view';
import { getById, getSectionsApi } from '/react4xp/parts/knowledge-base-common/actions';
import ParentLink from '/react4xp/parts/knowledge-base/components/parent-link';

export const MobileContext = createContext(false);

const KnowledgeBase = () => {
    const articleFromUrl = useRef<NoContentArticle | null>(null);
    const [isMobile, setIsMobile] = useState<boolean>(null);
    const { serviceUrl } = useContext(ComponentContext);

    useEffect(() => {
        setIsMobile(document.documentElement.clientWidth < 800);
        const resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                if (entry.contentRect && entry.contentRect.width) {
                    setIsMobile(entry.contentRect.width < 800);
                }
            }
        });

        resizeObserver.observe(document.documentElement);

        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    const sections = useKbEntity<Section>({
        getAction: getSectionsApi,
        serviceUrl,
    });

    const categories = useKbEntity<Category>({
        getAction: getCategoriesApi,
        serviceUrl,
    });

    const tags = useKbEntity<Tag>({
        getAction: getTagsApi,
        serviceUrl,
    });

    const articles = useKbEntity<NoContentArticle>({
        getAction: getArticlesApi,
        serviceUrl,
    });

    useEffect(() => {
        const params = location.search.slice(1).split('&');
        const articleId = params.find((param) => param.startsWith('article='))?.split('=')[1];

        const initialFetch = () => {
            sections.fetchItems();
            tags.fetchItems();
        };

        if (articleId) {
            getById<NoContentArticle>(serviceUrl, articleId).then((article) => {
                articleFromUrl.current = article;
                initialFetch();
            });
        } else {
            initialFetch();
        }
    }, []);

    useEffect(() => {
        if (sections.items?.length) {
            const activeSection = articleFromUrl.current
                ? sections.items.find(
                      (section) => section.id === articleFromUrl.current.path.split('/').at(-3)
                  )
                : sections.items[0];
            sections.setActive(activeSection);
        }
    }, [sections.items]);

    useEffect(() => {
        if (sections?.active) {
            categories.fetchItems(sections.active);
        }
        categories.resetActive();
        articles.resetActive();
        tags.resetActive();
    }, [sections.active]);

    useEffect(() => {
        if (categories.items?.length) {
            const activeCategory = articleFromUrl.current
                ? categories.items.find(
                      (category) => category.id === articleFromUrl.current.path.split('/').at(-2)
                  )
                : categories.items[0];
            if (articleFromUrl.current || !isMobile) {
                categories.setActive(activeCategory);
            }
        }
    }, [categories.items]);

    useEffect(() => {
        if (categories?.active) {
            if (articleFromUrl.current) {
                articles.fetchItems(categories.active).then(() => {
                    tags.setActiveById(articleFromUrl.current.tagId);
                    articles.setActive(articleFromUrl.current);
                    articleFromUrl.current = null;
                });
            } else {
                articles.fetchItems(categories.active);
            }
        }
        articles.resetActive();
        tags.resetActive();
    }, [categories.active]);

    const articlesByTag = useMemo(
        () =>
            articles.items.reduce((acc, article) => {
                const tagArticles = acc.get(article.tagId);
                tagArticles?.push(article);
                return acc;
            }, new Map<string, NoContentArticle[]>(tags.items.map((tag) => [tag.id, []]))),
        [articles.items, tags.items]
    );

    const search = useSearch({
        resetTriggers: [sections.active, categories.active],
        onSearch: articles.setActive,
        serviceUrl,
    });

    return (
        <MobileContext.Provider value={isMobile}>
            <div className="knowledge-base half-back mb-5">
                <div className="container">
                    <Row
                        justify="space-between"
                        gutter={20}
                        align="top"
                    >
                        <Col flex="auto">
                            <SearchBar {...search} />
                        </Col>
                    </Row>

                    <Divider />

                    {search.results ? (
                        <Space
                            direction="vertical"
                            style={{ display: 'flex' }}
                        >
                            <ParentLink
                                name={
                                    tags.active?.title ||
                                    categories.active?.title ||
                                    sections.active?.title
                                }
                                goBack={search.clear}
                            />
                            <SearchResultsView
                                articles={search.results}
                                query={search.query}
                                tags={tags.items}
                            />
                        </Space>
                    ) : (
                        <>
                            <SectionSelector
                                sections={sections.items}
                                activeSection={sections.active}
                                selectSection={sections.setActive}
                            />
                            <Divider />
                            {categories.isLoading ? (
                                <Skeleton />
                            ) : (
                                <CategoryList
                                    categories={categories.items}
                                    activeCategory={categories.active}
                                    selectCategory={categories.setActive}
                                >
                                    {articles.isLoading || search.isLoading ? (
                                        <Skeleton />
                                    ) : articles.active ? (
                                        <Space
                                            direction="vertical"
                                            style={{ display: 'flex' }}
                                        >
                                            <ParentLink
                                                name={tags.active?.title}
                                                goBack={articles.resetActive}
                                            />
                                            <ArticleView
                                                article={articles.active}
                                                section={sections.active?.title}
                                                category={categories.active?.title}
                                                tag={tags.active?.title}
                                            />
                                        </Space>
                                    ) : tags.active ? (
                                        <Space
                                            direction="vertical"
                                            style={{ display: 'flex' }}
                                        >
                                            <ParentLink
                                                name={categories.active?.title}
                                                goBack={tags.resetActive}
                                            />
                                            <TagView
                                                articles={articlesByTag.get(tags.active.id)}
                                                setActiveArticle={articles.setActive}
                                                tag={tags.active}
                                            />
                                        </Space>
                                    ) : categories.active ? (
                                        <Space
                                            direction="vertical"
                                            style={{ display: 'flex' }}
                                        >
                                            {isMobile && (
                                                <ParentLink
                                                    name={sections.active?.title}
                                                    goBack={categories.resetActive}
                                                />
                                            )}
                                            {articles.items.length ? (
                                                <CategoryView
                                                    selectTag={tags.setActive}
                                                    tags={tags.items}
                                                    articlesByTag={articlesByTag}
                                                />
                                            ) : (
                                                <Empty />
                                            )}
                                        </Space>
                                    ) : null}
                                </CategoryList>
                            )}
                        </>
                    )}
                </div>
            </div>
        </MobileContext.Provider>
    );
};

type ComponentContextType = {
    clientUrl: string;
    serviceUrl: string;
    translations: Record<string, string>;
    commonTranslations: Record<string, string>;
};
export const ComponentContext = createContext<ComponentContextType>(null);

export default (props: any) => (
    <PartWrapper {...props}>
        <ComponentContext.Provider value={...props}>
            <KnowledgeBase />
        </ComponentContext.Provider>
    </PartWrapper>
);
